import callDialogHtml from '../callDialog.html';

export const dialogMixin = {

    //********************* SHOW CALL DIALOG ********************************//
    // Opens the bottom-sheet GhDialog for either direction; further updates while
    // the call is up go through updateDialogState/attachMediaStreams instead of
    // re-opening the dialog.
    // * otherUser (Object|null) — { fullname, avatar_128, avatar_512 }
    // * callState (String) — what the dialog shows first, the call's own state
    //   by default. 'connecting' is display-only: a call answered on the native
    //   screen, while this side sets it up (see nativeCallsMixin).
    showCallDialog({ otherUser, callState = this.callState }) {
        const ghDialog = gudhub.ghconstructor.angularInjector.get('GhDialog');

        ghDialog.show({
            position: 'bottom',
            toolbar: false,
            class: 'gh_call_dialog_wrapper',
            clickOutsideToClose: false,
            template: { toolbar: '', content: callDialogHtml },
            controller: ['$scope', ($scope) => {
                this.dialogScope = $scope;
                $scope.callState = callState;
                $scope.callVideo = this.callVideo;
                $scope.otherUserName = otherUser?.fullname || 'Unknown';
                $scope.otherUserAvatar = otherUser?.avatar_128 || otherUser?.avatar_512 || '';
                $scope.isMuted = false;
                $scope.isCameraOff = false;
                $scope.isExpanded = false;

                $scope.accept = () => this.acceptIncomingCall();
                $scope.reject = () => this.rejectIncomingCall();
                $scope.cancelOutgoing = () => this.cancelOutgoingCall();
                $scope.hangup = () => this.hangupCall();
                $scope.mute = () => this.toggleMute();
                $scope.toggleCamera = () => this.toggleCamera();
                $scope.nativeCall = Boolean(this.nativeCallView);
                $scope.toggleExpanded = () => {
                    $scope.isExpanded = !$scope.isExpanded;
                };
            }]
        }).then(() => {}, () => {
            this.dialogScope = null;
        });

        // Opened from SIP.js and Capacitor callbacks, outside any Angular
        // digest - without one the dialog would sit unrendered until
        // something else happened to run a digest.
        this.dialogScope?.$applyAsync();
    },

    //********************* CLOSE CALL DIALOG ********************************//
    closeCallDialog() {
        if(!this.dialogScope) return;
        this.dialogScope.hide();
        this.dialogScope = null;
    },

    //********************* UPDATE DIALOG STATE ********************************//
    // * patch (Object) — properties to merge onto the dialog's $scope (e.g. { callState, isMuted })
    updateDialogState(patch) {
        if(!this.dialogScope) return;
        // $apply() throws "$apply already in progress" when called from
        // mute()/toggleCamera() etc, since ng-click already wraps those in
        // their own digest - the error was silently uncaught, so the state
        // patch (isMuted/isCameraOff/...) never actually reached the scope
        // or the DOM. $applyAsync() safely defers to the next digest
        // instead of throwing when one's already running.
        this.dialogScope.$applyAsync(() => Object.assign(this.dialogScope, patch));
    },

    //********************* ATTACH MEDIA STREAMS ********************************//
    // Pulls local/remote tracks off the active session's peerConnection into the
    // <video> elements rendered by the dialog template. Single-active-call
    // assumption (enforced by the call state machine) makes the un-namespaced
    // querySelector safe - only one .gh_call_dialog can exist at a time.
    attachMediaStreams() {
        const sessionDescriptionHandler = this.session?.sessionDescriptionHandler;
        const peerConnection = sessionDescriptionHandler?.peerConnection;
        if(!peerConnection) return;

        // SIP.js's SDH maintains remoteMediaStream itself and keeps adding
        // tracks as they arrive - safer than a one-shot getReceivers()
        // snapshot, which permanently misses any track that shows up after
        // Established (seen live as "RTP delivered but nothing plays" on the
        // answering side). Fall back to the snapshot for safety.
        let remoteStream = sessionDescriptionHandler.remoteMediaStream;
        if(!remoteStream) {
            remoteStream = new MediaStream();
            peerConnection.getReceivers().forEach((receiver) => {
                if(receiver.track) remoteStream.addTrack(receiver.track);
            });
        }

        const localStream = new MediaStream();
        peerConnection.getSenders().forEach((sender) => {
            if(sender.track) localStream.addTrack(sender.track);
        });
        // teardownCall stops sessionDescriptionHandler.localMediaStream's
        // tracks (not the peer-connection-sender-derived localStream above):
        // if the far end's SDP answer rejects an m-line (e.g. FreeSWITCH
        // rejecting video on an audio-only profile), that track never becomes
        // a sender, so it'd never show up in getSenders() - but it was still
        // captured from the camera/mic via getUserMedia and stays live,
        // holding the hardware open, unless stopped via this full reference.
        this._localStream = sessionDescriptionHandler.localMediaStream;

        const remoteVideo = document.querySelector('.gh_call_dialog__remote_video');
        const localVideo = document.querySelector('.gh_call_dialog__local_video');
        // Explicit play() instead of trusting the autoplay attribute: if the
        // browser blocks/skips autoplay at srcObject-assignment time (element
        // still display:none behind ng-show, or transient activation already
        // consumed), the element stays paused forever and never retries on
        // its own - RTP keeps arriving but nothing is heard. Confirmed live
        // as the "packets delivered, recipient hears nothing" failure mode.
        if(remoteVideo) {
            remoteVideo.srcObject = remoteStream;
            remoteVideo.play().catch((err) => console.warn('Remote media playback failed', err));
            // If tracks arrive after Established (late video, renegotiation),
            // kick playback again - a paused element does not resume itself.
            remoteStream.onaddtrack = () => {
                remoteVideo.play().catch((err) => console.warn('Remote media playback failed', err));
            };
        }
        if(localVideo) {
            localVideo.srcObject = localStream;
            localVideo.play().catch(() => {});
        }
    },
};
